import { useSetAtom } from 'jotai';
import { useEffect } from 'react';

import { chronicleAudioRmsAtom } from '@/atoms/chronicle';
import { chronicleBridge } from '@/tauri/chronicle';
import { listenChronicleAudioRms } from '@/tauri/chronicle/events';
import { chronicleLogger as logger } from '@/utils/logger/chronicle-logger';

/**
 * Subscribe to Chronicle audio RMS events for real-time visualization.
 * Mounted once by ChronicleStateProvider.
 */
export function useChronicleAudioRmsListener() {
  const setAudioRms = useSetAtom(chronicleAudioRmsAtom);

  useEffect(() => {
    if (!chronicleBridge.isAvailable()) return;

    let unlistenFn: (() => void) | null = null;
    // APP-7607: guard against the async listen() resolving AFTER this effect
    // has been torn down, so the resolved Tauri listener does not leak across
    // remounts / view churn.
    let cancelled = false;

    const setup = async () => {
      try {
        const unlisten = await listenChronicleAudioRms((event) => {
          setAudioRms(event.payload);
        });
        if (cancelled) {
          unlisten();
          return;
        }
        unlistenFn = unlisten;
      } catch (error) {
        logger.error({
          msg: 'Failed to setup Chronicle audio RMS listener',
          error: error instanceof Error ? error.message : String(error),
        });
      }
    };

    void setup();

    return () => {
      cancelled = true;
      unlistenFn?.();
    };
  }, [setAudioRms]);
}
